/* eslint-disable no-param-reassign, class-methods-use-this */
const DEFAULTS = {
  // How far (px) a card must travel before release dismisses it.
  distanceThreshold: 120,
  // How fast (px/ms) a flick must be to dismiss regardless of distance.
  velocityThreshold: 0.6,
  // Distance (px) used to map drag distance onto the fade curve.
  fadeDistance: 260,
  // Lowest opacity a card fades to while being dragged.
  minOpacity: 0.3,
  // Visual offset/scale applied to each card sitting behind the top one.
  stackOffset: 14,
  stackScale: 0.03,
  stackRotation: 3,
  // How many stacked cards remain visible behind the top card.
  maxVisible: 3,
  // How long (ms) a card takes to glide away when dismissed via a button/keyboard.
  buttonDismissDuration: 900,
  // Delay (ms) between each card settling back when the deck is reset.
  resetStagger: 200,
};

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function parseNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

class SwipeCards {
  constructor(element) {
    if (!(element instanceof HTMLElement)) {
      throw new TypeError('Invalid element');
    }

    this.root = element;
    this.deck = element.querySelector('[data-swipe-deck]');
    this.prevBtn = element.querySelector('[data-swipe-prev]');
    this.nextBtn = element.querySelector('[data-swipe-next]');
    this.resetBtns = Array.from(element.querySelectorAll('[data-swipe-reset]'));

    if (!this.deck) {
      return;
    }

    this.config = SwipeCards.parseConfig(element.dataset);
    this.reducedMotion = prefersReducedMotion();

    // Original DOM order — the source of truth for a reset.
    this.allCards = Array.from(this.deck.querySelectorAll('[data-swipe-card]'));
    // Cards currently in play, index 0 === top card.
    this.stack = this.allCards.slice();
    // Dismissed cards, most-recently removed last (used for prev/restore).
    this.dismissed = [];

    this.pointerId = null;
    this.activeCard = null;
    this.drag = null;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.allCards.forEach((card) => {
      card.style.setProperty('--card-base-rotation', `${SwipeCards.baseRotation(card)}deg`);
    });

    this.deck.addEventListener('pointerdown', this.onPointerDown);
    this.root.addEventListener('keydown', this.onKeyDown);

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.restore());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.dismissTop(
        this.alternatingDirection(),
        { duration: this.config.buttonDismissDuration },
      ));
    }
    this.resetBtns.forEach((btn) => btn.addEventListener('click', () => this.reset()));

    // TEMP: manual hint trigger for testing — remove before launch.
    (element.querySelectorAll('[data-swipe-hint]') ?? []).forEach((btn) => btn.addEventListener('click', () => {
      this.cancelHint();
      this.playHint();
    }));

    this.hintAnimation = null;
    this.hintObserver = null;

    this.layout();
    this.initHint();
  }

  static parseConfig(dataset) {
    return {
      distanceThreshold: parseNumber(dataset.swipeDistance, DEFAULTS.distanceThreshold),
      velocityThreshold: parseNumber(dataset.swipeVelocity, DEFAULTS.velocityThreshold),
      fadeDistance: parseNumber(dataset.swipeFade, DEFAULTS.fadeDistance),
      minOpacity: DEFAULTS.minOpacity,
      stackOffset: DEFAULTS.stackOffset,
      stackScale: DEFAULTS.stackScale,
      stackRotation: DEFAULTS.stackRotation,
      maxVisible: parseNumber(dataset.swipeVisible, DEFAULTS.maxVisible),
      buttonDismissDuration: parseNumber(dataset.swipeButtonDuration, DEFAULTS.buttonDismissDuration),
      resetStagger: parseNumber(dataset.swipeResetStagger, DEFAULTS.resetStagger),
    };
  }

  // A consistent, slightly-random base tilt per card so the deck looks hand-stacked.
  static baseRotation(card) {
    if (card.dataset.rotation !== undefined) {
      return parseNumber(card.dataset.rotation, 0);
    }

    const index = Number(card.dataset.index ?? 0);
    const pattern = [-3, 4, -2, 3, -4, 2];
    return pattern[index % pattern.length];
  }

  get topCard() {
    return this.stack[0] ?? null;
  }

  // A locked card (the end/CTA card) sits in the stack but can't be dragged away.
  static isLocked(card) {
    return Boolean(card) && card.hasAttribute('data-swipe-locked');
  }

  // Once, when the deck scrolls into view, nudge the top card right-and-back a
  // couple of times to signal that the stack is swipeable.
  initHint() {
    if (this.reducedMotion
      || typeof IntersectionObserver !== 'function'
      || typeof this.deck.animate !== 'function') {
      return;
    }

    this.hintObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          this.playHint();
          this.hintObserver.disconnect();
          this.hintObserver = null;
        }
      });
    }, { threshold: 0.6 });

    this.hintObserver.observe(this.root);
  }

  playHint() {
    const card = this.topCard;

    if (!card || SwipeCards.isLocked(card) || typeof card.animate !== 'function') {
      return;
    }

    const rot = parseNumber(card.style.getPropertyValue('--card-base-rotation'), 0);
    // Anchor start/end to the card's actual resting transform so there's no
    // jump when the animation finishes and reverts to the inline style.
    const rest = card.style.transform || `translate3d(0, 0, 0) rotate(${rot}deg)`;
    const pull = `translate3d(100px, 0, 0) rotate(${rot + 6}deg)`;

    this.hintAnimation = card.animate(
      [
        { transform: rest, offset: 0 },
        { transform: pull, offset: 0.5 },
        { transform: rest, offset: 1 },
      ],
      { duration: 1300, easing: 'linear' },
    );
  }

  cancelHint() {
    if (this.hintAnimation) {
      this.hintAnimation.cancel();
      this.hintAnimation = null;
    }
  }

  // Keep focusables in sync with aria-hidden so hidden cards are never tabbable.
  static syncCardFocusables(card, interactive) {
    card.querySelectorAll('a[href], button, input, select, textarea, [tabindex]').forEach((el) => {
      if (interactive) {
        if (el.hasAttribute('data-swipe-tabindex')) {
          const prev = el.getAttribute('data-swipe-tabindex');
          if (prev === '') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', prev);
          }
          el.removeAttribute('data-swipe-tabindex');
        }

        if (el.hasAttribute('data-swipe-disabled')) {
          el.disabled = false;
          el.removeAttribute('data-swipe-disabled');
        }
        return;
      }

      if (!el.hasAttribute('data-swipe-tabindex')) {
        el.setAttribute(
          'data-swipe-tabindex',
          el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '',
        );
      }
      el.setAttribute('tabindex', '-1');

      if ('disabled' in el && !el.disabled) {
        el.disabled = true;
        el.setAttribute('data-swipe-disabled', '');
      }
    });
  }

  layout() {
    const {
      stackOffset, stackScale, stackRotation, maxVisible,
    } = this.config;

    this.stack.forEach((card, i) => {
      const isTop = i === 0;
      const depth = Math.min(i, maxVisible);
      const hidden = i > maxVisible;
      const rotation = isTop
        ? card.style.getPropertyValue('--card-base-rotation') || '0deg'
        : `${(i % 2 === 0 ? -1 : 1) * stackRotation}deg`;

      card.classList.toggle('is-top', isTop);
      card.classList.remove('is-dragging', 'is-dismissed');
      card.style.zIndex = String(this.stack.length - i);
      card.style.opacity = hidden ? '0' : '1';
      card.style.pointerEvents = isTop ? 'auto' : 'none';
      card.setAttribute('aria-hidden', isTop ? 'false' : 'true');
      SwipeCards.syncCardFocusables(card, isTop);
      card.style.transform = isTop
        ? `translate3d(0, 0, 0) rotate(${rotation})`
        : `translate3d(0, ${depth * stackOffset}px, 0) scale(${1 - depth * stackScale}) rotate(${rotation})`;
    });

    // Dismissed cards stay in the DOM — keep their focusables inert too.
    this.dismissed.forEach((card) => {
      card.setAttribute('aria-hidden', 'true');
      SwipeCards.syncCardFocusables(card, false);
    });

    this.updateControls();
  }

  updateControls() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.dismissed.length === 0;
    }
    if (this.nextBtn) {
      // Nothing left to dismiss once only the locked end card remains.
      this.nextBtn.disabled = !this.topCard || SwipeCards.isLocked(this.topCard);
    }
  }

  onPointerDown(event) {
    if (this.pointerId !== null || this.stack.length === 0) {
      return;
    }

    const card = event.target.closest('[data-swipe-card]');

    if (!card || card !== this.topCard || SwipeCards.isLocked(card)) {
      return;
    }

    this.cancelHint();

    this.pointerId = event.pointerId;
    this.activeCard = card;
    this.drag = {
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      lastTime: performance.now(),
      velocityX: 0,
      velocityY: 0,
    };

    card.classList.add('is-dragging');

    try {
      card.setPointerCapture(event.pointerId);
    } catch (error) {
      // setPointerCapture can throw if the pointer is already gone; safe to ignore.
    }

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId || !this.drag || !this.activeCard) {
      return;
    }

    const now = performance.now();
    const dt = Math.max(now - this.drag.lastTime, 1);

    this.drag.velocityX = (event.clientX - this.drag.lastX) / dt;
    this.drag.velocityY = (event.clientY - this.drag.lastY) / dt;
    this.drag.lastX = event.clientX;
    this.drag.lastY = event.clientY;
    this.drag.lastTime = now;

    const dx = event.clientX - this.drag.startX;
    const dy = event.clientY - this.drag.startY;
    const distance = Math.hypot(dx, dy);
    const base = parseNumber(this.activeCard.style.getPropertyValue('--card-base-rotation'), 0);
    const rotation = base + dx * 0.06;
    const opacity = Math.max(
      this.config.minOpacity,
      1 - (distance / this.config.fadeDistance) * (1 - this.config.minOpacity),
    );

    this.activeCard.style.opacity = String(opacity);
    this.activeCard.style.transform = `translate3d(${dx}px, ${dy}px, 0) rotate(${rotation}deg)`;
  }

  onPointerUp(event) {
    if (event.pointerId !== this.pointerId || !this.drag || !this.activeCard) {
      return;
    }

    const card = this.activeCard;
    const dx = event.clientX - this.drag.startX;
    const dy = event.clientY - this.drag.startY;
    const distance = Math.hypot(dx, dy);
    const speed = Math.hypot(this.drag.velocityX, this.drag.velocityY);
    const shouldDismiss = distance > this.config.distanceThreshold
      || speed > this.config.velocityThreshold;

    this.teardownPointer(card, event.pointerId);

    if (shouldDismiss) {
      // Fly off along the direction of travel (or the flick, if barely moved).
      const dir = distance > 4
        ? { x: dx, y: dy }
        : { x: this.drag.velocityX, y: this.drag.velocityY };
      this.dismiss(card, dir);
    } else {
      this.snapBack(card);
    }

    this.pointerId = null;
    this.activeCard = null;
    this.drag = null;
  }

  teardownPointer(card, pointerId) {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);

    try {
      card.releasePointerCapture(pointerId);
    } catch (error) {
      // Ignore — capture may already be released.
    }
  }

  snapBack(card) {
    card.classList.remove('is-dragging');
    card.style.transitionDuration = '';
    card.style.opacity = '1';
    const rotation = card.style.getPropertyValue('--card-base-rotation') || '0deg';
    card.style.transform = `translate3d(0, 0, 0) rotate(${rotation})`;
  }

  dismiss(card, direction, { duration } = {}) {
    const magnitude = Math.hypot(direction.x, direction.y) || 1;
    const throwDistance = Math.max(window.innerWidth, window.innerHeight) * 1.5;
    const x = (direction.x / magnitude) * throwDistance;
    const y = (direction.y / magnitude) * throwDistance;
    const rotation = parseNumber(card.style.getPropertyValue('--card-base-rotation'), 0)
      + (direction.x >= 0 ? 18 : -18);

    card.classList.remove('is-dragging');
    card.classList.add('is-dismissed');
    card.style.pointerEvents = 'none';
    // A longer duration (from buttons/keyboard) makes the card glide off gently
    // instead of snapping away like a fast flick.
    card.style.transitionDuration = duration ? `${duration}ms` : '';

    // Remove from the live stack immediately so the next card becomes interactive.
    this.stack = this.stack.filter((c) => c !== card);
    this.dismissed.push(card);

    const finish = () => {
      card.removeEventListener('transitionend', finish);
      card.style.visibility = 'hidden';
    };

    if (this.reducedMotion) {
      card.style.transform = 'translate3d(0, 0, 0)';
      card.style.opacity = '0';
      finish();
    } else {
      requestAnimationFrame(() => {
        card.style.opacity = '0';
        card.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rotation}deg)`;
      });
      card.addEventListener('transitionend', finish);
    }

    this.layout();
  }

  // Alternate the throw direction each dismissal (right, left, right, ...).
  // Based on how many cards are already gone so Prev/restore stays in sync.
  alternatingDirection() {
    const toLeft = this.dismissed.length % 2 === 1;
    return { x: toLeft ? -1 : 1, y: -0.15 };
  }

  dismissTop(direction, options) {
    if (!this.topCard || SwipeCards.isLocked(this.topCard)) {
      return;
    }

    this.dismiss(this.topCard, direction, options);
  }

  restore() {
    const card = this.dismissed.pop();

    if (!card) {
      return;
    }

    card.style.visibility = '';
    card.style.transitionDuration = '';
    card.classList.remove('is-dismissed');
    this.stack.unshift(card);
    this.layout();
  }

  reset() {
    this.cancelHint();
    this.dismissed.length = 0;
    this.stack = this.allCards.slice();

    const { maxVisible } = this.config;
    const step = this.config.resetStagger;

    // Snap each card back to full opacity (transitions off) while it's still
    // off-screen, so it flies back in solid rather than fading in.
    this.stack.forEach((card) => {
      card.classList.remove('is-dismissed', 'is-dragging');
      card.style.visibility = '';
      card.style.transition = 'none';
      card.style.opacity = '1';
    });

    // Flush the opacity change before re-enabling transitions.
    this.reflow = this.deck.offsetWidth;

    // Stagger each card flying back to its resting spot (bottom-up).
    this.stack.forEach((card, i) => {
      const delay = i > maxVisible ? 0 : (maxVisible - i) * step;
      card.style.transition = '';
      card.style.transitionDuration = '';
      card.style.transitionDelay = `${delay}ms`;
    });

    this.layout();

    // Clear the stagger delays once the rebuild is done so later swipes are snappy.
    const settleTime = (maxVisible + 1) * step + 700;
    window.setTimeout(() => {
      this.allCards.forEach((card) => {
        card.style.transitionDelay = '';
      });
    }, settleTime);

    if (this.topCard) {
      this.topCard.focus({ preventScroll: true });
    }
  }

  onKeyDown(event) {
    if (this.stack.length === 0 && event.key !== 'Enter') {
      return;
    }

    if (event.key === 'ArrowRight') {
      // Right = Next (dismiss the top card, alternating throw direction).
      event.preventDefault();
      this.dismissTop(this.alternatingDirection(), { duration: this.config.buttonDismissDuration });
    } else if (event.key === 'ArrowLeft') {
      // Left = Prev (bring the last dismissed card back).
      event.preventDefault();
      this.restore();
    }
  }

  destroy() {
    this.cancelHint();
    this.hintObserver?.disconnect();
    this.deck.removeEventListener('pointerdown', this.onPointerDown);
    this.root.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }
}

export default SwipeCards;
